import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Check, X, Clock, Minus, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CELL_MAP = {
  compliant:     { icon: Check, label: "Compliant", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  expiring:      { icon: Clock, label: "Expiring",  bg: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-300" },
  expired:       { icon: X,     label: "Expired",   bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200" },
  missing:       { icon: X,     label: "Missing",   bg: "bg-red-50/50",  text: "text-red-700",     border: "border-red-100" },
  in_progress:   { icon: Clock, label: "In progress", bg: "bg-sky-50",   text: "text-sky-700",     border: "border-sky-200" },
  not_completed: { icon: Minus, label: "Not done",  bg: "bg-slate-50",   text: "text-slate-500",   border: "border-slate-200" },
  not_required:  { icon: Minus, label: "N/A",       bg: "bg-slate-50",   text: "text-slate-300",   border: "border-slate-100" },
};

function Cell({ cell }) {
  const s = CELL_MAP[cell.status] || CELL_MAP.not_required;
  const Icon = s.icon;
  return (
    <div
      className={`flex items-center justify-center w-full h-full py-1.5 border rounded-md ${s.bg} ${s.text} ${s.border}`}
      title={`${s.label}${cell.expiry_date ? ` · exp ${cell.expiry_date}` : ""}`}
      data-testid={`matrix-cell-${cell.status}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
  );
}

export default function MatrixTab({ onOpenStaff, refreshTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mandatory");
  const [siteId, setSiteId] = useState("");
  const [role, setRole] = useState("");
  const [staffId, setStaffId] = useState("");
  const [sites, setSites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { course_scope: scope };
      if (siteId) params.site_id = siteId;
      if (role) params.role = role;
      if (staffId) params.staff_id = staffId;
      const r = await api.get("/training/matrix", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [scope, siteId, role, staffId]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {}); }, []);

  return (
    <section className="bg-white border border-slate-200 rounded-lg" data-testid="training-matrix-tab">
      <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900">Training Matrix</h2>
          <p className="text-xs text-slate-500 mt-0.5">Live cross-view of staff (rows) against required courses (columns). Cells reflect the most recent training record.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="matrix-scope" className="h-8 text-xs border border-slate-300 rounded-md bg-white px-2 capitalize">
            <option value="required">Required (mandatory + role)</option>
            <option value="mandatory">Mandatory only</option>
            <option value="all">All active courses</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="btn-matrix-filters" className={`h-8 text-xs ${showFilters ? "bg-slate-100" : ""}`}>
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="p-4 border-b border-slate-200 grid grid-cols-2 md:grid-cols-3 gap-3 bg-slate-50">
          <FilterSelect label="Site" value={siteId} onChange={setSiteId} options={sites.map((s) => ({ v: s.id, label: s.name }))} testid="filter-matrix-site" />
          <FilterSelect label="Role" value={role} onChange={setRole} options={[
            {v: "care_worker", label: "Care Worker"},
            {v: "manager", label: "Manager"},
            {v: "deputy_manager", label: "Deputy Manager"},
            {v: "service_manager", label: "Service Manager"},
            {v: "registered_manager", label: "Registered Manager"},
            {v: "admin", label: "Admin"},
          ]} testid="filter-matrix-role" />
          <FilterSelect label="Staff member" value={staffId} onChange={setStaffId} options={(data?.rows || []).map((r) => ({ v: r.user_id, label: r.user_name }))} testid="filter-matrix-staff" />
        </div>
      )}

      {loading ? (
        <div className="p-8 text-sm text-slate-500">Loading matrix…</div>
      ) : !data?.rows?.length ? (
        <div className="p-16 text-center text-sm text-slate-500">No staff members match the current filters.</div>
      ) : (
        <div className="overflow-x-auto p-4">
          <div className="mb-3 flex items-center gap-3 text-[11px] text-slate-600">
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-600" /> Compliant</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber-600" /> Expiring</span>
            <span className="flex items-center gap-1"><X    className="w-3.5 h-3.5 text-red-600" /> Expired / Missing</span>
            <span className="flex items-center gap-1"><Minus className="w-3.5 h-3.5 text-slate-400" /> Not required</span>
          </div>
          <table className="text-xs border-collapse w-full" data-testid="training-matrix">
            <thead>
              <tr>
                <th className="text-left px-2 py-2 sticky left-0 bg-white z-10 border-b border-slate-200 min-w-[220px]">Staff Member</th>
                {data.columns.map((c) => (
                  <th
                    key={c.id}
                    className="px-1.5 py-2 border-b border-slate-200 text-left align-bottom"
                    style={{ minWidth: 80 }}
                    title={`${c.name} · ${c.category || ""} · ${c.requirement_type}`}
                  >
                    <div className="rotate-[-35deg] origin-bottom-left inline-block w-24 truncate font-medium text-slate-700">{c.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`matrix-row-${r.user_id}`}>
                  <td className="px-2 py-1.5 sticky left-0 bg-white z-10 border-b border-slate-100">
                    <button
                      className="text-slate-900 hover:text-sky-700 hover:underline text-left font-medium"
                      onClick={() => onOpenStaff?.(r.user_id)}
                      data-testid={`matrix-staff-${r.user_id}`}
                    >{r.user_name}</button>
                    <div className="text-[10px] text-slate-500 capitalize">{(r.user_role || "").replace(/_/g, " ")}</div>
                  </td>
                  {r.cells.map((cell, ix) => (
                    <td key={ix} className="px-1 py-1 border-b border-slate-100"><Cell cell={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full h-8 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
